import React, { useState, useEffect } from "react"
import { X, Loader2, Users } from "lucide-react"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function ViewRegistrantsModal({ open, onClose, event }) {
  const [registrants, setRegistrants] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [expandedRow, setExpandedRow] = useState(null)

  useEffect(() => {
    async function fetchRegistrants() {
      setIsLoading(true)
      setError("")
      try {
        const token = localStorage.getItem("token")
        const res = await fetch(`${API}/api/admin/events/${event.id}/registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) throw new Error("Failed to fetch registrants")
        const data = await res.json()
        setRegistrants(data)
      } catch (err) {
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    if (open && event) {
      fetchRegistrants()
    } else {
      setRegistrants([])
    }
  }, [open, event])

  if (!open || !event) return null



  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex h-full max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink-900 shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-ink-800/50 px-6 py-4">
          <div>
            <h2 className="font-display text-xl font-bold text-paper-0">Event Registrants</h2>
            <p className="text-sm text-mist-400">{event.title} • {event.date}</p>
          </div>
          <button onClick={onClose} className="text-mist-400 hover:text-paper-0 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stats Row */}
        <div className="flex shrink-0 items-center gap-6 border-b border-white/10 bg-ink-800/20 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-mist-500">Total Swimmers</p>
              <p className="font-display text-xl font-bold text-paper-0">{registrants.length}</p>
            </div>
          </div>

        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
            </div>
          ) : registrants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="mb-4 h-12 w-12 text-mist-600" />
              <h3 className="font-display text-lg font-bold text-paper-0">No Registrations Yet</h3>
              <p className="mt-1 text-sm text-mist-400">Swimmers will appear here once they register.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-left text-sm text-mist-300 min-w-[600px]">
                <thead className="bg-ink-800/50 text-xs uppercase text-mist-400">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Swimmer</th>
                    <th className="px-6 py-4 font-semibold">Sub-Events</th>

                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {registrants.map(reg => (
                    <React.Fragment key={reg.id}>
                      <tr 
                        onClick={() => setExpandedRow(prev => prev === reg.id ? null : reg.id)}
                        className={`cursor-pointer transition-colors hover:bg-white/[0.04] ${expandedRow === reg.id ? "bg-white/[0.02]" : ""}`}
                      >
                        <td className="px-6 py-4">
                          <p className="font-semibold text-paper-0">{reg.swimmer_name}</p>
                          <p className="text-xs text-mist-500">{reg.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-semibold text-mist-300">
                            {reg.sub_events ? reg.sub_events.length : 0} event{(reg.sub_events && reg.sub_events.length === 1) ? "" : "s"}
                          </span>
                        </td>

                      </tr>
                      {expandedRow === reg.id && (
                        <tr className="bg-ink-900/50">
                          <td colSpan={2} className="border-t-0 p-0">
                            <div className="px-6 py-4 border-l-2 border-gold-500">
                              {reg.sub_events && reg.sub_events.length > 0 ? (
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                  {reg.sub_events.map(se => (
                                    <div key={se.id} className="flex flex-col gap-1 rounded-lg border border-white/5 bg-ink-800 p-3">
                                      <span className="text-sm font-semibold text-paper-0">{se.name}</span>
                                      <div className="flex items-center justify-between mt-1">
                                        <span className="text-xs font-mono text-mist-400">
                                          Seed: <span className="text-paper-0">{se.seed_time || "N/A"}</span>
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-mist-500">No sub-events selected.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
